import { CreativeMapItem, MediaType } from '../../redux/creative';

// Ad servers a creative tag can be generated for.
export const AD_SERVER_MAP: Record<string, string> = {
  ft: 'Flashtalking'
};

// A selectable audio-companion banner offered during tag creation.
export interface AudioCompanionOption {
  adid: string;
  assetid: string;
  title: string;
}

// Reasons a creative is ineligible for a given tag/placement. Ported from
// bpm-frontend's useWorkspaceOptions predicate (StreamingCreatives.jsx). Platform
// and placement-type are two distinct media-type gates (see below).
export const IneligibilityReason = {
  PLACEMENT_TYPE_MISMATCH: 'placement-type-mismatch',
  PLATFORM_MISMATCH: 'platform-mismatch',
  AUDIO_SUBTYPE_MISMATCH: 'audio-subtype-mismatch',
  MISSING_FT_ID: 'missing-ft-id',
  MISSING_FT_PIXEL_ID: 'missing-ft-pixel-id',
  DIMENSIONS_MISMATCH: 'dimensions-mismatch',
  LENGTH_MISMATCH: 'length-mismatch',
  NOT_LIVE: 'not-live'
} as const;
export type IneligibilityReasonType =
  (typeof IneligibilityReason)[keyof typeof IneligibilityReason];

// Placement types whose Flashtalking creative is tracked by a pixel ID rather
// than a standard Flashtalking ID. Pixel-style placements are media-agnostic.
const PIXEL_PLACEMENT_TYPES = ['Multi-tracker', 'Pixel'];

// Maps a tag's platform string to the creative media_type it requires. A platform
// may be a compound label (e.g. "Streaming Video"), so we substring-match. Device
// names like "CTV"/"Mobile"/"Desktop" match nothing here and impose no platform
// constraint (their media is inferred from the placement type instead).
const PLATFORM_MEDIA_TYPES: { platform: string; mediaType: MediaType }[] = [
  { platform: 'Streaming', mediaType: 'streaming' },
  { platform: 'Audio', mediaType: 'audio' },
  { platform: 'Display', mediaType: 'display' }
];

// The media type each placement type requires. Pixel / Multi-tracker are
// tracking-only and impose no media-type requirement (they go with anything).
const PLACEMENT_TYPE_MEDIA_TYPES: Partial<Record<string, MediaType>> = {
  Audio: 'audio',
  VAST: 'streaming',
  Display: 'display',
  HTML: 'display'
};

// The media type a flight's platform represents, used to gate which placement
// types are compatible. Device-level platforms (CTV/Mobile/Desktop) are
// streaming video; everything else falls back to streaming.
export const getPlatformMediaType = (platform: string): MediaType => {
  if (platform.includes('Audio')) return 'audio';
  if (platform.includes('Display')) return 'display';
  return 'streaming';
};

// The platform-required media type the creative is missing (substring match), or
// null. Independent of placement type.
const getMissingPlatformMediaType = (
  creative: CreativeMapItem,
  platform: string
): MediaType | null => {
  for (const { platform: name, mediaType } of PLATFORM_MEDIA_TYPES) {
    if (platform.includes(name) && !creative.media_types?.includes(mediaType)) {
      return mediaType;
    }
  }
  return null;
};

// The placement-type-required media the creative is missing (e.g. VAST →
// streaming), or null. Pixel/Multi-tracker impose nothing.
const getMissingPlacementMediaType = (
  creative: CreativeMapItem,
  placementType?: string
): MediaType | null => {
  const required = placementType ? PLACEMENT_TYPE_MEDIA_TYPES[placementType] : undefined;
  if (!required) return null;
  return creative.media_types?.includes(required) ? null : required;
};

// For the tag form: a placement type is incompatible with the flight when its
// required media differs from the flight's media (Pixel is always allowed).
// Returns a tooltip explaining the block, or undefined when allowed.
export const getPlacementTypeDisabledReason = (
  placementValue: string,
  platform: string
): string | undefined => {
  const required = PLACEMENT_TYPE_MEDIA_TYPES[placementValue];
  if (!required) return undefined;
  const flightMedia = getPlatformMediaType(platform);
  if (required === flightMedia) return undefined;
  return `${platform} is a ${flightMedia} flight — this placement is for ${required}`;
};

interface EligibilityContext {
  isci: string;
  date: string;
  platform: string;
  isLive: boolean;
  length?: string;
  dimensions?: string;
  placementType?: string;
  adServer?: string;
}

// Returns null when the creative is eligible for the tag, otherwise the first
// failing reason. Placement-type and platform media are checked separately so
// the surfaced reason names which gate failed.
export const getIneligibilityReason = (
  creative: CreativeMapItem,
  { isci, platform, isLive, length, dimensions, placementType, adServer }: EligibilityContext
): IneligibilityReasonType | null => {
  if (getMissingPlacementMediaType(creative, placementType) !== null) {
    return IneligibilityReason.PLACEMENT_TYPE_MISMATCH;
  }

  if (getMissingPlatformMediaType(creative, platform) !== null) {
    return IneligibilityReason.PLATFORM_MISMATCH;
  }

  // Radio and Streaming Audio are separate creatives, never interchangeable on one ISCI (unlike
  // Linear/Streaming Video). This flow only ever deals in Streaming Audio, so once a creative has
  // already passed the media-type gates above, a Radio avail is unconditionally wrong here.
  if (creative.media_types?.includes('audio') && creative.avail === 'L') {
    return IneligibilityReason.AUDIO_SUBTYPE_MISMATCH;
  }

  if (adServer === 'Flashtalking' || adServer === 'ft') {
    const usesPixelId = PIXEL_PLACEMENT_TYPES.includes(placementType ?? '');
    if (usesPixelId && creative.flashtalking_pixel_id == null) {
      return IneligibilityReason.MISSING_FT_PIXEL_ID;
    }
    if (!usesPixelId && creative.flashtalking_id == null) {
      return IneligibilityReason.MISSING_FT_ID;
    }
  }

  if (placementType === 'Display' && dimensions != null) {
    const matchesDimensions = isci.includes(dimensions) || creative.dimensions === dimensions;
    if (!matchesDimensions) return IneligibilityReason.DIMENSIONS_MISMATCH;
  }

  if (length && String(creative.length ?? 0) !== length) {
    return IneligibilityReason.LENGTH_MISMATCH;
  }

  if (!isLive) return IneligibilityReason.NOT_LIVE;

  return null;
};

const mediaLabel = (creative: CreativeMapItem): string =>
  (creative.media_types ?? []).join('/') || 'unknown';

// User-facing tooltip text explaining why a creative is disabled. Each message
// names the specific gate that failed and the creative's actual media type.
export const getIneligibilityLabel = (
  reason: IneligibilityReasonType,
  creative: CreativeMapItem,
  { date, platform, length, dimensions, placementType }: EligibilityContext
): string => {
  switch (reason) {
    case IneligibilityReason.PLACEMENT_TYPE_MISMATCH: {
      const required = getMissingPlacementMediaType(creative, placementType);
      return `Placement type mismatch — ${placementType} needs a ${required} creative (this is ${mediaLabel(creative)})`;
    }
    case IneligibilityReason.PLATFORM_MISMATCH: {
      const required = getMissingPlatformMediaType(creative, platform);
      return `Platform mismatch — this ${platform} flight needs a ${required} creative (this is ${mediaLabel(creative)})`;
    }
    case IneligibilityReason.AUDIO_SUBTYPE_MISMATCH:
      return 'Media type mismatch — this is a Radio creative, but this placement needs Streaming';
    case IneligibilityReason.MISSING_FT_ID:
      return 'Missing Flashtalking ID';
    case IneligibilityReason.MISSING_FT_PIXEL_ID:
      return 'Missing Flashtalking pixel ID';
    case IneligibilityReason.DIMENSIONS_MISMATCH:
      return `Creative dimensions (${creative.dimensions ?? 'none'}) don't match ${dimensions}`;
    case IneligibilityReason.LENGTH_MISMATCH:
      return `Creative is ${creative.length}s, but this placement needs ${length}s`;
    case IneligibilityReason.NOT_LIVE:
      return `Creative isn't live on ${date}`;
    default:
      return 'Not eligible for this placement';
  }
};
